import { useState } from 'react';
import type { FormEvent } from 'react';
import type { RecipeImportPrefill } from '@mlc/shared';
import { importFromPhotos } from './api';
import { Banner, Button, Card } from './ui';

/**
 * Import photo (US-32), décidé au §1 de CLAUDE.md et jamais implémenté :
 * « un seul appel à un modèle multimodal, image en entrée, JSON recette en
 * sortie ». Beaucoup de recettes personnelles sont sur papier — c'est le cœur
 * émotionnel d'un « livre de cuisine ».
 *
 * L'écran ne fait que collecter des images : le résultat passe par le **même**
 * écran de validation que les autres imports, aucun import ne s'enregistrant
 * silencieusement (§3.4 étape 4).
 */

/** Une recette de livre tient souvent sur deux pages ; au-delà de quatre, c'est un chapitre. */
const MAX_PHOTOS = 4;

const ACCEPTED = 'image/jpeg,image/png,image/webp';

/** Contenu d'un fichier en base64, sans le préfixe `data:` que l'API n'attend pas. */
function toBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : '';
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.onerror = () => { reject(new Error('Photo illisible.')); };
    reader.readAsDataURL(file);
  });
}

export interface ImportPhotoFormProps {
  onImported: (prefill: RecipeImportPrefill) => void;
  onCancel: () => void;
}

export function ImportPhotoForm({ onImported, onCancel }: ImportPhotoFormProps): React.JSX.Element {
  const [files, setFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (files.length === 0 || submitting) return;

    setSubmitting(true);
    setError(null);
    Promise.all(files.map((file) => toBase64(file).then((base64) => ({ mime: file.type, base64 }))))
      .then((images) => importFromPhotos({ images }))
      .then(onImported)
      .catch((err: unknown) => { setError(err instanceof Error ? err.message : 'Erreur inconnue.'); })
      .finally(() => { setSubmitting(false); });
  }

  return (
    <form onSubmit={handleSubmit} aria-label="Photographier une recette" className="flex flex-col gap-4">
      {error !== null && <Banner tone="danger">{error}</Banner>}

      <Card surface className="flex flex-col gap-4">
        <div className="flex items-start gap-3">
          <svg
            viewBox="0 0 24 24"
            aria-hidden="true"
            className="mt-0.5 size-6 shrink-0 text-primary"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M4 8h3l1.5-2h7L17 8h3a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1Z" />
            <circle cx="12" cy="13" r="3.5" />
          </svg>
          <div className="flex flex-col gap-1">
            <p className="font-medium text-foreground">Photographiez la page</p>
            <p className="text-sm text-foreground-secondary">
              Un livre, une fiche écrite à la main, une page arrachée d’un magazine. Vous pourrez tout relire et
              corriger avant d’enregistrer.
            </p>
          </div>
        </div>

        <label className="flex flex-col gap-2 text-sm">
          <span>Photos de la recette (jusqu’à {MAX_PHOTOS})</span>
          <input
            type="file"
            accept={ACCEPTED}
            multiple
            // `capture` absent volontairement : sur téléphone, le choix entre
            // appareil photo et pellicule appartient au système, et le forcer
            // empêcherait de reprendre une photo déjà prise.
            onChange={(e) => {
              const chosen = [...(e.target.files ?? [])].slice(0, MAX_PHOTOS);
              setFiles(chosen);
              setError(null);
            }}
            className="min-h-touch text-sm"
          />
        </label>

        {files.length > 0 && (
          <ul className="flex flex-wrap gap-2">
            {files.map((file) => (
              <li key={file.name} className="text-meta">
                {file.name}
              </li>
            ))}
          </ul>
        )}

        <p className="text-meta">
          Une recette sur deux pages ? Ajoutez les deux photos d’un coup : elles sont lues ensemble.
        </p>
      </Card>

      <div className="flex flex-wrap gap-3">
        <Button type="submit" variant="primary" disabled={files.length === 0 || submitting} aria-busy={submitting}>
          Lire la recette
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel}>
          Annuler
        </Button>
      </div>
    </form>
  );
}
